import { useCallback, useEffect, useRef, useState } from 'react'
import { useFocusEffect, useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { AppStackParamList } from '@src/navigation/types'
import { LegalPolicyConsentKind } from '@src/types'
import { useDispatchAsync, useSnack } from '@src/utils'
import { LegalPolicyConsentStatusesQuery } from '../graphql/legalPolicyConsentStatuses.generated'

export const AI_PROCESSING_CONSENT_ERROR = 'Failed to verify your AI consent, please try again.'

/**
 * Unlike useGetRequiredPolicyConsents, a failed status fetch rejects instead of resolving to "nothing
 * required", so callers can fail closed.
 */
export const useGetRequiredAiProcessingPolicy = () => {
  const dispatch = useDispatchAsync()

  const getRequiredAiProcessingPolicy = useCallback(async () => {
    const { statuses } = await dispatch<
      LegalPolicyConsentStatusesQuery['legalPolicyConsentStatuses']
    >({
      type: 'policyConsents/statuses',
    })

    return statuses.find(
      (status) => status.required && status.kind === LegalPolicyConsentKind.AiProcessing,
    )?.policy
  }, [dispatch])

  return { getRequiredAiProcessingPolicy }
}

/**
 * Checks fresh AI processing consent status every time the screen gains focus and, when consent is
 * required, opens the PolicyConsent screen for it.
 *
 * When the user comes back from the consent screen without accepting, `onDeclined` is called instead
 * of prompting again (so the user is never stuck in a prompt loop). The next focus prompts again.
 * If the status can't be fetched, access stays blocked and `onDeclined` is called.
 */
export const useAiProcessingConsentGate = ({ onDeclined }: { onDeclined?: () => void } = {}) => {
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const showSnack = useSnack()
  const { getRequiredAiProcessingPolicy } = useGetRequiredAiProcessingPolicy()
  const [consented, setConsented] = useState(false)
  const promptedRef = useRef(false)
  const onDeclinedRef = useRef(onDeclined)

  useEffect(() => {
    onDeclinedRef.current = onDeclined
  })

  useFocusEffect(
    useCallback(() => {
      let active = true
      const returningFromConsent = promptedRef.current
      promptedRef.current = false

      getRequiredAiProcessingPolicy()
        .then((policy) => {
          if (!active) {
            return
          }

          if (!policy) {
            setConsented(true)
            return
          }

          setConsented(false)

          if (returningFromConsent) {
            onDeclinedRef.current?.()
            return
          }

          promptedRef.current = true
          navigation.navigate('PolicyConsent', { policy })
        })
        .catch(() => {
          if (!active) {
            return
          }

          setConsented(false)
          showSnack(AI_PROCESSING_CONSENT_ERROR, null, 'error')
          onDeclinedRef.current?.()
        })

      return () => {
        active = false
      }
    }, [getRequiredAiProcessingPolicy, navigation, showSnack]),
  )

  return { consented }
}
